import { ReactNode } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Supplier } from "@/gql/schema";
import { formatPurchasableExtraPrice } from "@/app/Suppliers/Utils/formatPurchasableExtraPrice";
import { stripHtml } from "@/lib/Utils/string";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import {
  purchasableExtrasQuery,
  PurchasableExtraListResult,
} from "@/app/Suppliers/GraphQL/supplierPurchasableExtrasQuery";
import { useSupplierPurchasableExtrasHook } from "@/app/Suppliers/Hooks/useSupplierPurchasableExtrasHook";
import { SupplierPurchasableExtraCreateDialog } from "@/app/Suppliers/Components/SupplierPurchasableExtraCreateDialog";
import { SupplierPurchasableExtraUpdateDialog } from "@/app/Suppliers/Components/SupplierPurchasableExtraUpdateDialog";

export function SupplierPurchasableExtraGrid({
  supplier,
  addLabel = "Add extra",
}: {
  supplier: Pick<Supplier, "currency" | "id">;
  addLabel?: string;
}) {
  const getActions = useSupplierPurchasableExtrasHook();
  const { open: openCreate } = useDialog(SupplierPurchasableExtraCreateDialog);
  const { open: openEdit } = useDialog(SupplierPurchasableExtraUpdateDialog);

  const { data } = useGqlQuery(purchasableExtrasQuery, {
    first: 100,
    supplierId: supplier.id,
  });

  const extras = data?.supplierPurchasableExtras.data ?? [];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {extras.map((extra) => (
        <ExtraCard
          key={extra.id}
          extra={extra}
          currency={supplier.currency}
          actions={<IconMenuActions actions={getActions(extra)} />}
          onEdit={() =>
            openEdit({
              supplierPurchasableExtra: extra,
              currency: extra.supplier.currency,
            })
          }
        />
      ))}

      <button
        type="button"
        onClick={() => openCreate({ supplierId: supplier.id })}
        className="flex min-h-[132px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 p-4 text-sm font-semibold text-gray-500 transition hover:border-indigo-300 hover:bg-indigo-50/40 hover:text-indigo-600 focus:outline-none focus-visible:border-indigo-400"
      >
        <PlusIcon className="size-6" />
        {addLabel}
      </button>
    </div>
  );
}

function ExtraCard({
  extra,
  currency,
  actions,
  onEdit,
}: {
  extra: PurchasableExtraListResult;
  currency: Supplier["currency"];
  actions: ReactNode;
  onEdit: () => void;
}) {
  const description = extra.description ? stripHtml(extra.description) : null;

  return (
    <div className="relative flex flex-col rounded-lg border border-gray-200 bg-white transition hover:border-gray-300 hover:shadow-sm">
      <div className="absolute right-2 top-2">{actions}</div>

      <button
        type="button"
        onClick={onEdit}
        className="flex flex-1 flex-col p-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
      >
        <span className="truncate pr-6 font-semibold text-gray-900">
          {extra.name}
        </span>

        {description ? (
          <p className="mt-1 line-clamp-2 text-sm text-gray-500">
            {description}
          </p>
        ) : null}

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs text-gray-500">Units available</dt>
            <dd className="mt-0.5 font-medium text-gray-900">
              {extra.units_purchasable}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Unit price</dt>
            <dd className="mt-0.5 font-medium text-gray-900">
              {formatPurchasableExtraPrice(extra.unit_price, currency)}
            </dd>
          </div>
        </dl>
      </button>
    </div>
  );
}
